import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useI18n } from 'builder/hooks/useI18n'
import FocusManager from 'builder/services/FocusManager'
import { SectionNames, selectors, actions } from 'builder/modules/resumeEditor'
import RichTextArea from 'builder/components/RichTextArea'
import {
  SectionBodyFoldableContainer,
  SectionHeader,
  SectionHeaderFoldableContainer,
  SectionHint,
  useFoldableSectionScrollFix,
} from 'builder/components/Section'
import { TunerSectionTooltips } from 'builder/components/Tuner'
import SectionTitle, { SectionTitleBadgeProps } from 'builder/components/SectionTitle'
import { useUpdateSimpleField } from 'builder/hooks/useUpdateSimpleField'
import { useRenameSection } from 'builder/hooks/useRenameSection'
import { AiKeywordsToggler, useKeywords } from 'builder/components/AiFeatures/Keywords'
import { SectionSuggestionBadge } from 'builder/components/SuggestionBadge'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useTailoringBannerTriggerConfig } from 'builder/hooks/useTailoringBannerTriggerConfig'
import { RichTextEditorInstanceWrapper } from '../RichTextArea/types'
import { TailorResumeBanner } from '../ImproveResumePanel'
import { SectionContainer, SectionContent } from './styles'
import { useProfileSummarySuggestions } from './hooks/useProfileSummarySuggestions'

const OPTIMIZER_BANNER_LETTERS_THRESHOLD = 100

type Props = {
  isStepByStepFlow?: boolean
  isFoldable?: boolean
}

const ProfileSummary = ({ isStepByStepFlow, isFoldable = true }: Props) => {
  const { i18n } = useI18n()
  const editorInstanceRef = useRef<RichTextEditorInstanceWrapper>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const { updateSimpleField } = useUpdateSimpleField()
  const { renameSection } = useRenameSection()
  const { isKeywordsAvailable } = useKeywords()
  const resume = useSelector(selectors.resume)
  const { shouldAnimateEditorFieldContent } = useTypedSelector(selectors.aiProfile)
  const { bannerTriggeredFrom } = useSelector(selectors.optimizerSection)
  const { profile: value, sectionTitles = {}, locale = '', skipOptimizerBanner } = resume || {}
  const customTitle = sectionTitles.profile || ''
  const textValue = value || ''
  const { isBannerTriggerAvailable } = useTailoringBannerTriggerConfig()
  const isOpened = useTypedSelector(store => selectors.isSectionOpened(store, SectionNames.profile))
  const elementRef = useRef<HTMLDivElement>(null)

  const { additionalToolbarActions, onFocus } = useProfileSummarySuggestions({
    editorInstanceRef,
    editorContainerRef,
    isStepByStepFlow,
  })

  useEffect(() => {
    FocusManager.subscribe(SectionNames.profile, () => {
      if (editorInstanceRef.current) editorInstanceRef.current.focusEditor()
    })

    return () => FocusManager.unsubscribe(SectionNames.profile)
  }, [])

  useFoldableSectionScrollFix({
    elementRef,
    isOpened,
  })

  const handleDescriptionChange = useCallback(
    (value: string | null) => updateSimpleField({ name: 'profile', value, debounce: true }),
    [updateSimpleField],
  )

  const handleRename = useCallback(
    (value: string | null) => {
      if (!value) return
      renameSection({ id: SectionNames.profile, value })
    },
    [renameSection],
  )

  const recommendedRangeProps = {
    ranges: {
      bad: {
        start: 1,
        end: 100,
      },
      good: {
        start: 400,
        end: 600,
      },
    },
    recommendedRangeLabel: i18n.t('builder.resume_editor.professional_summary.length_suggestion', {
      count: '400-600',
    }),
  }

  const badgeComponents = useMemo<FC<SectionTitleBadgeProps>[]>(
    () => [
      props => {
        return isKeywordsAvailable ? (
          <AiKeywordsToggler section={SectionNames.profile} {...props} />
        ) : null
      },
      props => <SectionSuggestionBadge sectionName="profile" {...props} />,
    ],
    [isKeywordsAvailable],
  )

  const onAiProfileSummaryAnimationEnd = useCallback(() => {
    dispatch(actions.resetAIProfileSummaryFlow())
  }, [dispatch])

  const onTextLengthChange = useCallback(
    (length: number) => {
      if (
        !isBannerTriggerAvailable ||
        skipOptimizerBanner ||
        (bannerTriggeredFrom && bannerTriggeredFrom !== 'profile')
      ) {
        return
      }

      if (length >= OPTIMIZER_BANNER_LETTERS_THRESHOLD) {
        dispatch(actions.setOptimizerBannerTriggeredFrom('profile'))
      } else {
        dispatch(actions.setOptimizerBannerTriggeredFrom(null))
      }
    },
    [dispatch, skipOptimizerBanner, bannerTriggeredFrom, isBannerTriggerAvailable],
  )

  const handleToggle = useCallback(() => {
    if (!isFoldable) return
    dispatch(actions.setOpenedSections({ [SectionNames.profile]: !isOpened }))
  }, [dispatch, isFoldable, isOpened])

  return (
    <SectionContainer
      data-highlight-id={SectionNames.profile}
      ref={elementRef}
      data-testid="profile-section"
    >
      <SectionHeader isOpened={isOpened} onClick={handleToggle} isStepByStepFlow={isStepByStepFlow}>
        <SectionTitle
          title={i18n.t('builder.resume_editor.professional_summary.title')}
          customTitle={customTitle}
          onRename={handleRename}
          renamable
          badgeComponents={badgeComponents}
          isOpened={isOpened}
          isFoldable={!isStepByStepFlow}
        />
        <SectionHeaderFoldableContainer isOpened={isOpened}>
          <SectionHint>{i18n.t('builder.resume_editor.professional_summary.label')}</SectionHint>
        </SectionHeaderFoldableContainer>
      </SectionHeader>
      <SectionBodyFoldableContainer isOpened={isOpened} isStepByStepFlow={isStepByStepFlow}>
        <SectionContent>
          <RichTextArea
            instanceRef={editorInstanceRef}
            containerRef={editorContainerRef}
            placeholder={i18n.t('builder.resume_editor.professional_summary.placeholder')}
            value={textValue}
            onChange={handleDescriptionChange}
            locale={locale}
            isAnimating={shouldAnimateEditorFieldContent}
            onAnimationEnd={onAiProfileSummaryAnimationEnd}
            onTextLengthChange={onTextLengthChange}
            additionalToolbarActions={additionalToolbarActions}
            onFocus={onFocus}
            {...recommendedRangeProps}
          />
        </SectionContent>

        <TunerSectionTooltips sectionId={SectionNames.profile} />

        <TailorResumeBanner
          isVisible={bannerTriggeredFrom === 'profile'}
          transitionProps={{ marginTop: 16 }}
        />
      </SectionBodyFoldableContainer>
    </SectionContainer>
  )
}

export default memo(ProfileSummary)
